import type { User } from "@bezoned/shared/fluid/office-schema-factory.js";
import { useStableCallback } from "@bezoned/shared/hooks/use-stable-callback.js";
import { computeCallIndicatorColor } from "@bezoned/shared/utils/call-indicator-color.js";
import { isDemoUserId } from "@bezoned/shared/utils/demo-users.js";
import { AvatarGroupItem } from "@fluentui/react-components";
import type { PresenceBadgeStatus } from "@fluentui/react-components";
import { useLayoutEffect } from "react";

import {
  getTableIdFromUser,
  isUserInAreaWithoutVoiceCall,
} from "../../../../features/fluid/fluid.service.js";
import { useFluid } from "../../../../features/fluid/hooks/use-fluid.js";
import { useCurrentUserTablePresence } from "../../../../features/presence/use-my-table-presence.js";
import { useUserPresence } from "../../../../features/presence/use-user-presence.js";
import { AvatarSpeakingIndicator } from "../../../../features/table-call/avatar-speaking-indicator.js";
import { renderedAvatarUserIdsStore } from "../../../../features/table-call/rendered-avatar-user-ids.store.js";
import { useTableCallParticipant } from "../../../../features/table-call/use-table-call-participant.js";
import { useTableCall } from "../../../../features/table-call/use-table-call.js";
import { useAvatarMenu } from "../../../../hooks/use-avatar-menu.js";
import { useUserPhoto } from "../../../../hooks/use-user-photo.js";

interface UserAvatarProps {
  user: User;
}

const INDICATOR_COLOR_CLASS_MAP = {
  issue: "yellow-300",
  muted: "purple-200",
  "not-in-call": "gray-200",
  unmuted: "purple-300",
} as const;

export const UserAvatar = ({ user }: UserAvatarProps) => {
  const isDummyUser = isDemoUserId(user.id);
  const { fluidUserId, sharedTree } = useFluid();
  const { tableId: currentUserTableId } = useCurrentUserTablePresence();
  const { avatar } = useUserPhoto({ msUserId: user.id });
  const presence = useUserPresence({ msUserId: user.id });
  const { showAvatarMenu } = useAvatarMenu();
  const { meeting, status, microphoneActive } = useTableCall();
  const { remote } = useTableCallParticipant(isDummyUser ? "" : user.id);

  const isCurrentUser = fluidUserId === user.id;
  const isAtSameTable =
    getTableIdFromUser({ sharedTree, userId: user.id }) === currentUserTableId;
  const isInSpecialArea = isUserInAreaWithoutVoiceCall({
    sharedTree,
    userId: user.id,
  });
  const isMuted = isCurrentUser
    ? !microphoneActive
    : Boolean(remote ? !remote.audioEnabled : true);
  const remotePhase = remote?.phase;
  const isVisibleInContext = isCurrentUser || isAtSameTable || isInSpecialArea;
  const connected = isCurrentUser
    ? status === "connected" && !!meeting
    : status === "connected" && !!meeting && remotePhase === "ready";

  useLayoutEffect(() => {
    if (isDummyUser) {
      return;
    }

    renderedAvatarUserIdsStore.add(user.id);

    return () => {
      renderedAvatarUserIdsStore.remove(user.id);
    };
  }, [isDummyUser, user.id]);

  const indicatorColor = () => {
    // Users at a different table always show the default color since
    // we have no call-level visibility into their state.
    if (!isVisibleInContext) {
      return INDICATOR_COLOR_CLASS_MAP.muted;
    }

    const color = computeCallIndicatorColor({
      audioTrackUnhealthy: false,
      callStatus: status,
      connected,
      hasMeeting: !!meeting,
      isInAreaWithoutVoiceCall: isInSpecialArea,
      isMuted,
      isSelf: isCurrentUser,
    });

    return INDICATOR_COLOR_CLASS_MAP[color];
  };
  const handleClick = useStableCallback((event) => {
    event.stopPropagation();

    // Demo users don't have an avatar menu.
    if (isDemoUserId(user.id)) {
      return;
    }

    showAvatarMenu(user.id, {
      x: event.clientX,
      y: event.clientY,
    });
  });
  const handleContextMenu = useStableCallback((event) => {
    event.preventDefault();
    event.stopPropagation();
  });

  const avatarPresence = isDummyUser ? user.presence : presence;

  return (
    <section className="relative">
      <section
        className={`rounded-full bg-bzd-${isDummyUser ? INDICATOR_COLOR_CLASS_MAP.unmuted : indicatorColor()} relative z-10 shadow-md duration-150 ease-in-out`}
      >
        <section className="relative z-10 flex items-center justify-center rounded-full p-1">
          <AvatarGroupItem
            badge={
              avatarPresence
                ? {
                    outOfOffice: avatarPresence?.outOfOffice?.isOutOfOffice,
                    status: avatarPresence?.availability as PresenceBadgeStatus,
                  }
                : null
            }
            image={avatar}
            key={user.id}
            name={user.name}
            onContextMenu={handleContextMenu}
            onClick={handleClick}
            style={{
              cursor: isDummyUser ? "default" : "pointer",
            }}
          />
        </section>
      </section>
      {Boolean(meeting) && !isDummyUser && (
        <AvatarSpeakingIndicator
          connected={connected}
          isMuted={isMuted}
          isVisibleInContext={isVisibleInContext}
          userId={user.id}
        />
      )}
    </section>
  );
};
